import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Table,
  Spin,
  notification,
  Button,
  Modal,
  Input,
  InputNumber,
  DatePicker,
  Row,
  Col,
  Space,
  Select,
  Tabs,
  Checkbox,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { setSideTab } from "../../../toolkit/slices/inventories/inventorySlice";
import formatIndianNumber from "../../../../utils/formatIndianNumber";
import generatePurchaseOrderPDF, {
  DEFAULT_PO_PDF_FILTERS,
  buildPurchaseOrderPdfDoc,
} from "../../../../utils/generatePurchaseOrderPDF";
import { DownloadOutlined } from "@ant-design/icons";
import { BsDownload, BsEye } from "react-icons/bs";

dayjs.extend(customParseFormat);

// Plugin: when user types 20022026, parse as 20-02-2026 (same dayjs instance used by Ant Design)
dayjs.extend((option, dayjsClass) => {
  const proto = dayjsClass.prototype;
  const origParse = proto.parse;
  if (origParse) {
    proto.parse = function (cfg) {
      if (cfg && typeof cfg.date === "string") {
        const s = String(cfg.date).replace(/\s/g, "");
        if (/^\d{8}$/.test(s)) {
          cfg = {
            ...cfg,
            date: `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 8)}`,
          };
        }
      }
      return origParse.call(this, cfg);
    };
  }
});

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const PurchaseOrderContainer = () => {
  const dispatch = useDispatch();
  const [poList, setPoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [poHistoryModalOpen, setPoHistoryModalOpen] = useState(false);
  const [selectedPoHistory, setSelectedPoHistory] = useState([]);
  const [selectedPoHistoryPoNumber, setSelectedPoHistoryPoNumber] = useState("");
  const [selectedPoInventories, setSelectedPoInventories] = useState([]);
  const [selectedPoNumber, setSelectedPoNumber] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [expandFormValues, setExpandFormValues] = useState({});
  // Editing a PO History entry: { poId, index, expected } and its form values.
  const [editingHistory, setEditingHistory] = useState(null);
  const [historyForm, setHistoryForm] = useState({});
  const [savingHistory, setSavingHistory] = useState(false);
  const [savingPoId, setSavingPoId] = useState(null);
  const [addFormOpenForPoId, setAddFormOpenForPoId] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState("pending");
  const [parentCompanies, setParentCompanies] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedVendorName, setSelectedVendorName] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedSuffix, setSelectedSuffix] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  const [editVendorModalOpen, setEditVendorModalOpen] = useState(false);
  const [editingVendorPoId, setEditingVendorPoId] = useState(null);
  const [editingVendorName, setEditingVendorName] = useState(null);
  const [tradingPartyOptions, setTradingPartyOptions] = useState([]);
  const [tradingPartiesLoading, setTradingPartiesLoading] = useState(false);
  const [updatingVendor, setUpdatingVendor] = useState(false);
  const [poPdfModalOpen, setPoPdfModalOpen] = useState(false);
  const [poPdfContext, setPoPdfContext] = useState(null);
  const [poPdfFilters, setPoPdfFilters] = useState(() => ({
    ...DEFAULT_PO_PDF_FILTERS,
  }));
  const [poPdfPreviewOpen, setPoPdfPreviewOpen] = useState(false);
  const [poPdfPreviewUrl, setPoPdfPreviewUrl] = useState("");
  const poPdfPreviewRef = useRef(null);

  useEffect(() => {
    if (poPdfPreviewOpen && poPdfPreviewUrl && poPdfPreviewRef.current) {
      poPdfPreviewRef.current.src = poPdfPreviewUrl;
    }
  }, [poPdfPreviewOpen, poPdfPreviewUrl]);

  const toISO = (d) =>
    d && typeof d.toISOString === "function" ? d.toISOString() : null;
  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
  }, [dispatch]);

  const fetchFilterData = async () => {
    const token = window.localStorage.getItem("token");
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getFilterDataForPO`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success && result.data) {
        setParentCompanies(result.data.parentCompanies ?? []);
        setBrands(result.data.brands ?? []);
        setVendors(result.data.vendors ?? []);
      }
    } catch (error) {
      console.error("Failed to fetch filter options:", error);
    }
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchTradingParties = async () => {
    const token = window.localStorage.getItem("token");
    try {
      setTradingPartiesLoading(true);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllTradingParties`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setTradingPartyOptions(result.data);
      } else {
        setTradingPartyOptions([]);
        notification.warning({
          message: "No vendors",
          description: result.message || "No trading parties found.",
        });
      }
    } catch (error) {
      setTradingPartyOptions([]);
      notification.error({
        message: "Error",
        description: "Failed to fetch trading parties.",
      });
    } finally {
      setTradingPartiesLoading(false);
    }
  };

  const fetchAllPOs = async () => {
    const token = window.localStorage.getItem("token");
    const params = new URLSearchParams();

    if (activeStatusTab === "pending") params.set("isPending", "true");
    if (selectedParentCompanyId)
      params.set("parentCompanyId", selectedParentCompanyId);
    if (selectedBrandId) params.set("brandId", selectedBrandId);
    if (selectedVendorName) params.set("vendorName", selectedVendorName);
    if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
  params.set("suffix", selectedSuffix.join(",")); 
}
    if (dateRangeFilter && dateRangeFilter.length === 2) {
      params.set("startDate", dateRangeFilter[0].format("DD-MM-YYYY"));
      params.set("endDate", dateRangeFilter[1].format("DD-MM-YYYY"));
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    try {
      setLoading(true);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllPOs${query}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setPoList(result.data);
      } else {
        setPoList([]);
        notification.warning({
          message: "No data",
          description: result.message || "No purchase orders found.",
        });
      }
    } catch (error) {
      setPoList([]);
      notification.error({
        message: "Error",
        description: "Failed to fetch purchase orders.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    const token = window.localStorage.getItem("token");
    const params = new URLSearchParams();
    if (activeStatusTab === "pending") params.set("isPending", "true");
    if (selectedParentCompanyId)
      params.set("parentCompanyId", selectedParentCompanyId);
    if (selectedBrandId) params.set("brandId", selectedBrandId);
    if (selectedVendorName) params.set("vendorName", selectedVendorName);
  if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
  params.set("suffix", selectedSuffix.join(",")); 
}
    if (dateRangeFilter && dateRangeFilter.length === 2) {
      params.set("startDate", dateRangeFilter[0].format("DD-MM-YYYY"));
      params.set("endDate", dateRangeFilter[1].format("DD-MM-YYYY"));
    }
    params.set("excel", "true");
    const query = params.toString() ? `?${params.toString()}` : "";

    try {
      setDownloading(true);
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getAllPOs${query}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            authorization: `Token ${token}`,
          },
        },
      );

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();
      const contentDisp = res.headers.get("content-disposition") || "";
      let filename = "purchase_orders.xlsx";
      const m1 = contentDisp.match(/filename\*=(?:UTF-8'')?(.+)$/);
      const m2 = contentDisp.match(/filename="(.+)"/);
      if (m1) filename = decodeURIComponent(m1[1]);
      else if (m2) filename = m2[1];

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      notification.error({
        message: "Error",
        description: "Failed to download Excel.",
      });
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    fetchAllPOs();
  }, [
    activeStatusTab,
    selectedParentCompanyId,
    selectedBrandId,
    selectedVendorName,
    selectedSuffix,
    dateRangeFilter,
  ]);

  const getFormValuesForPo = (poId) =>
    expandFormValues[poId] ?? {
      invoiceNo: "",
      poAmount: "",
      startDate: null,
      endDate: null,
    };

  const setFormValueForPo = (poId, field, value) => {
    setExpandFormValues((prev) => ({
      ...prev,
      [poId]: {
        ...getFormValuesForPo(poId),
        [field]: value,
      },
    }));
  };

  const setFormDateRangeForPo = (poId, dates) => {
    setExpandFormValues((prev) => ({
      ...prev,
      [poId]: {
        ...(prev[poId] ?? {
          invoiceNo: "",
          poAmount: "",
          startDate: null,
          endDate: null,
        }),
        startDate: dates?.[0] ?? null,
        endDate: dates?.[1] ?? null,
      },
    }));
  };

  const toISOString = (val) => {
    if (!val) return "";
    if (typeof val.valueOf === "function")
      return new Date(val.valueOf()).toISOString();
    return moment(val).toISOString();
  };

  const handleUpdatePO = async (record) => {
    const poId = record._id;
    const form = getFormValuesForPo(poId);
    const invoiceNo = form.invoiceNo?.trim?.() ?? "";
    const poAmount = form.poAmount;
    const startDate = toISOString(form.startDate);
    const endDate = toISOString(form.endDate);

    const token = window.localStorage.getItem("token");
    try {
      setSavingPoId(poId);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/updatePO`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify({
            poId,
            invoiceNo,
            poAmount,
            startDate,
            endDate,
          }),
        },
      );
      const result = await response.json();
      if (result.success) {
        notification.success({
          message: "Updated",
          description: result.message || "PO updated successfully.",
        });
        setAddFormOpenForPoId(null);
        setExpandFormValues((prev) => {
          const next = { ...prev };
          delete next[poId];
          return next;
        });
        fetchAllPOs();
      } else {
        notification.error({
          message: "Update failed",
          description: result.message || "Failed to update PO.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update PO.",
      });
    } finally {
      setSavingPoId(null);
    }
  };

  const startEditHistory = (po, row, index) => {
    setAddFormOpenForPoId(null);
    setEditingHistory({
      poId: po._id,
      index,
      expected: { invoiceNo: row?.invoiceNo ?? "", poAmount: row?.poAmount ?? 0 },
    });
    setHistoryForm({
      poDate: row?.poDate ? dayjs(row.poDate) : null,
      invoiceNo: row?.invoiceNo ?? "",
      poAmount: row?.poAmount ?? null,
      startDate: row?.startDate ? dayjs(row.startDate) : null,
      endDate: row?.endDate ? dayjs(row.endDate) : null,
    });
  };

  const cancelEditHistory = () => {
    if (savingHistory) return;
    setEditingHistory(null);
    setHistoryForm({});
  };

  const handleSaveHistory = async () => {
    if (!editingHistory) return;
    const invoiceNo = (historyForm.invoiceNo ?? "").toString().trim();
    if (!invoiceNo) {
      notification.error({ message: "Invoice No. is required" });
      return;
    }
    if (
      historyForm.poAmount === null ||
      historyForm.poAmount === undefined ||
      historyForm.poAmount === ""
    ) {
      notification.error({ message: "PO Amount is required" });
      return;
    }
    if (!historyForm.startDate || !historyForm.endDate) {
      notification.error({ message: "Select the duration (start and end date)" });
      return;
    }
    const token = window.localStorage.getItem("token");
    try {
      setSavingHistory(true);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/editPOHistoryEntry`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify({
            poId: editingHistory.poId,
            index: editingHistory.index,
            expected: editingHistory.expected,
            invoiceNo,
            poAmount: historyForm.poAmount,
            poDate: historyForm.poDate
              ? toISOString(historyForm.poDate)
              : undefined,
            startDate: toISOString(historyForm.startDate),
            endDate: toISOString(historyForm.endDate),
          }),
        },
      );
      const result = await response.json();
      if (result.success) {
        notification.success({
          message: "Updated",
          description: result.message || "PO history entry updated.",
        });
        setEditingHistory(null);
        setHistoryForm({});
        fetchAllPOs();
      } else {
        notification.error({
          message: "Update failed",
          description:
            result.message || "Failed to update the PO history entry.",
        });
        // The entry was changed elsewhere: reload so the next edit starts from the latest data.
        if (response.status === 409) {
          setEditingHistory(null);
          fetchAllPOs();
        }
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update the PO history entry.",
      });
    } finally {
      setSavingHistory(false);
    }
  };

  const handleMarkPoAsFoc = (record) => {
    const poId = record?._id;
    if (!poId) return;

    Modal.confirm({
      title: "Mark PO as FOC?",
      content:
        "This will set Purchase Amount of all related trading inventories to zero.",
      okText: "Mark FOC",
      cancelText: "Cancel",
      onOk: async () => {
        const token = window.localStorage.getItem("token");
        try {
          setSavingPoId(poId);
          const response = await fetch(
            `${apiBaseUrl}/inventoryManagement/markPOAsFOC`,
            {
              method: "PUT",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                authorization: `Token ${token}`,
              },
              body: JSON.stringify({ poId }),
            },
          );
          const result = await response.json();
          if (result.success) {
            notification.success({
              message: "Updated",
              description: result.message || "PO marked as FOC successfully.",
            });
            fetchAllPOs();
          } else {
            notification.error({
              message: "Update failed",
              description: result.message || "Failed to mark PO as FOC.",
            });
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to mark PO as FOC.",
          });
        } finally {
          setSavingPoId(null);
        }
      },
    });
  };

  const handleViewInventories = (record) => {
    const inventories = record?.salesOrderId?.bdfId?.inventories ?? [];
    setSelectedPoInventories(inventories);
    setSelectedPoNumber(record?.poNumber ?? "PO");
    setViewModalOpen(true);
  };

  const handleViewPoHistory = (record) => {
    const poHistory = Array.isArray(record?.poHistory) ? record.poHistory : [];
    setSelectedPoHistory(poHistory);
    setSelectedPoHistoryPoNumber(record?.poNumber ?? "PO");
    setPoHistoryModalOpen(true);
  };

  const openEditVendorModal = async (record) => {
    setEditingVendorPoId(record?._id ?? null);
    setEditingVendorName(null);
    setEditVendorModalOpen(true);
    await fetchTradingParties();
  };

  const closeEditVendorModal = () => {
    setEditVendorModalOpen(false);
    setEditingVendorPoId(null);
    setEditingVendorName(null);
  };

  const closePoPdfFiltersModal = () => {
    setPoPdfModalOpen(false);
    setPoPdfContext(null);
  };

  const handlePoPdfDownload = async () => {
    const ctx = poPdfContext;
    if (!ctx) return;
    try {
      await generatePurchaseOrderPDF(
        ctx.tradingInventories,
        ctx.bdfDetails,
        ctx.poNumber,
        {
          ...poPdfFilters,
          isFromSubBdf: ctx.isFromSubBdf,
        },
      );
    } catch (err) {
      console.error("Failed to generate PO PDF:", err);
      notification.error({
        message: "Error",
        description: "Failed to generate PO PDF.",
      });
    }
  };

  const handlePoPdfPreview = async () => {
    const ctx = poPdfContext;
    if (!ctx) return;
    try {
      const doc = await buildPurchaseOrderPdfDoc(
        ctx.tradingInventories,
        ctx.bdfDetails,
        ctx.poNumber,
        {
          ...poPdfFilters,
          isFromSubBdf: ctx.isFromSubBdf,
        },
      );
      const pdfBlob = doc.output("blob");
      if (!pdfBlob) {
        notification.error({
          message: "Error",
          description: "Failed to generate PDF preview.",
        });
        return;
      }
      setPoPdfPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(pdfBlob);
      });
      setPoPdfPreviewOpen(true);
    } catch (err) {
      console.error("Failed to preview PO PDF:", err);
      notification.error({
        message: "Error",
        description: "Failed to generate PO PDF preview.",
      });
    }
  };

  const closePoPdfPreviewModal = () => {
    setPoPdfPreviewOpen(false);
    setPoPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const handleUpdateVendor = async () => {
    const token = window.localStorage.getItem("token");

    if (!editingVendorPoId || !editingVendorName) {
      notification.warning({
        message: "Select vendor",
        description: "Please select a vendor before updating.",
      });
      return;
    }

    try {
      setUpdatingVendor(true);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/editPOVendor`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify({
            poId: editingVendorPoId,
            vendor: editingVendorName,
          }),
        },
      );
      const result = await response.json();

      if (result.success) {
        notification.success({
          message: "Updated",
          description: result.message || "Vendor updated successfully.",
        });
        closeEditVendorModal();
        fetchAllPOs();
      } else {
        notification.error({
          message: "Update failed",
          description: result.message || "Failed to update vendor.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to update vendor.",
      });
    } finally {
      setUpdatingVendor(false);
    }
  };

  const stackedColumnTitle = (line1, line2) => (
    <span style={{ display: "inline-block", lineHeight: 1.25, whiteSpace: "normal" }}>
      {line1}
      <br />
      {line2}
    </span>
  );

  const columns = [
    {
      title: "PO No.",
      dataIndex: "poNumber",
      key: "poNumber",
      align: "center",
      width: 135,
      render: (text) => text || "-",
    },
    {
      title: "Date",
      dataIndex: "updatedAt",
      key: "updatedAt",
      align: "center",
      width: 115,
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Vendor",
      key: "vendor",
      align: "center",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <span>
            {record?.salesOrderId?.bdfId?.inventories?.[0]?.invId?.vendor ??
              record?.salesOrderId?.bdfId?.inventories?.[0]?.commissionForId
                ?.name ??
              "-"}
          </span>
          <Button type="link" style={{ padding: 0 }} onClick={() => openEditVendorModal(record)}>
            Edit
          </Button>
        </div>
      ),
    },
    {
      title: "Brand Name",
      key: "brandName",
      align: "center",
      render: (_, record) =>
        record?.salesOrderId?.bdfId?.brandId?.brandName ?? "-",
    },
    {
      title: "Sales Party Name",
      key: "salesPartyName",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (_, record) =>
        record?.salesOrderId?.bdfId?.billingPartyName ?? "-",
    },
    {
      title: stackedColumnTitle("Total PO", "Amount"),
      dataIndex: "totalPoAmount",
      key: "totalPoAmount",
      align: "center",
      width: 112,
      render: (amount) =>
        amount != null ? `₹ ${formatIndianNumber(amount)}` : "-",
    },
    {
      title: stackedColumnTitle("Pending PO", "Amount"),
      dataIndex: "pendingPoAmount",
      key: "pendingPoAmount",
      align: "center",
      width: 112,
      render: (amount) =>
        amount != null ? `₹ ${formatIndianNumber(amount)}` : "-",
    },
    {
      title: "Info",
      key: "info",
      align: "center",
      width: 72,
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Button type="link" onClick={() => handleViewInventories(record)}>
            View
          </Button>
        </div>
      ),
    },
    {
      title: "PO History",
      key: "poHistory",
      align: "center",
      width: 88,
      render: (_, record) => {
        const poHistory = Array.isArray(record?.poHistory) ? record.poHistory : [];
        if (poHistory.length === 0) {
          return (
            <div style={{ textAlign: "center", width: "100%" }}>-</div>
          );
        }
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Button type="link" onClick={() => handleViewPoHistory(record)}>
              View
            </Button>
          </div>
        );
      },
    },
    {
      title: (
        <span>
          <DownloadOutlined /> PO
        </span>
      ),
      key: "downloadPo",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          onClick={() => {
            const bdfDetails = record?.salesOrderId?.bdfId ?? null;
            const poNumber = record?.poNumber ?? "PO";
            const tradingInventories = (
              record?.salesOrderId?.bdfId?.inventories ?? []
            ).map((inv) =>
              inv.lineType
                ? inv
                : {
                    ...inv,
                    invId: {
                      ...(inv.invId || {}),
                      ownedBy: inv.invId?.vendor || "Unknown Vendor",
                    },
                  },
            );

            if (!tradingInventories.length) {
              notification.warning({
                message: "No inventories",
                description: "No trading inventories found for this PO.",
              });
              return;
            }

            if (!bdfDetails) {
              notification.warning({
                message: "No BDF",
                description: "BDF details not found for this PO.",
              });
              return;
            }

            setPoPdfFilters({ ...DEFAULT_PO_PDF_FILTERS });
            setPoPdfContext({
              tradingInventories,
              bdfDetails,
              poNumber,
              isFromSubBdf: Boolean(record?.salesOrderId?.isFromSub),
            });
            setPoPdfModalOpen(true);
          }}
        >
          PO
        </Button>
      ),
    },
    ...(activeStatusTab === "pending"
      ? [
          {
            title: "FOC",
            key: "markFoc",
            align: "center",
            width: 92,
            render: (_, record) => {
              const isAlreadyFoc =
                Number(record?.totalPoAmount || 0) === 0 &&
                Number(record?.pendingPoAmount || 0) === 0;
              return (
                <Button
                  type="link"
                  disabled={isAlreadyFoc}
                  loading={savingPoId === record?._id}
                  onClick={() => handleMarkPoAsFoc(record)}
                  style={
                    isAlreadyFoc ? undefined : { color: "#389e0d", padding: 0 }
                  }
                >
                  {isAlreadyFoc ? "FOC" : "Mark FOC"}
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  const CHARGE_TYPE_LABELS = {
    AGENCY_COMMISSION: "Agency Commission",
    TRANSPORTATION: "Transportation",
  };

  const inventoryModalColumns = [
    {
      title: "Location Name",
      key: "locationName",
      align: "center",
      render: (_, inv) =>
        inv?.lineType
          ? CHARGE_TYPE_LABELS[inv.lineType] || inv.lineType
          : (inv?.invId?.locations?.name ?? "-"),
    },
    {
      title: "Duration",
      key: "duration",
      align: "center",
      width: 240,
      render: (_, inv) => {
        const start = inv?.startDate
          ? moment(inv.startDate).format("DD/MM/YYYY")
          : "-";
        const end = inv?.endDate ? moment(inv.endDate).format("DD/MM/YYYY") : "-";

        return start !== "-" || end !== "-" ? `${start} to ${end}` : "-";
      },
    },
    {
      title: "Purchase Rate",
      key: "purchaseRate",
      align: "center",
      render: (_, inv) =>
        inv?.maintenanceRate != null
          ? `₹ ${formatIndianNumber(inv.maintenanceRate)}`
          : "-",
    },
  ];

  const CustomHeaderCell = (props) => (
    <th {...props} style={{ backgroundColor: "#daeffe", textAlign: "center" }}>
      {props.children}
    </th>
  );

  const PoHistoryHeaderCell = (props) => (
    <th {...props} style={{ backgroundColor: "#f0f9ff", textAlign: "center" }}>
      {props.children}
    </th>
  );

  const DATE_FORMAT = "DD-MM-YYYY";

  // Normalize 8-digit string to DD-MM-YYYY for parsing
  const normalizeDateString = (str) => {
    if (typeof str !== "string") return str;
    const s = str.replace(/\s/g, "");
    if (/^\d{8}$/.test(s))
      return `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 8)}`;
    return str;
  };

  // Ant Design 5 RangePicker uses dayjs – convert any stored value to dayjs for display
  const toDayjsOrNull = (val) => {
    if (!val) return null;
    if (dayjs.isDayjs?.(val)) return val;
    if (moment.isMoment(val)) return dayjs(val.valueOf());
    if (typeof val === "string") {
      const normalized = normalizeDateString(val);
      const d = dayjs(normalized, DATE_FORMAT);
      return d.isValid() ? d : null;
    }
    if (typeof val.valueOf === "function") return dayjs(val.valueOf());
    return null;
  };

  const poHistoryViewColumns = [
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      align: "center",
      render: (t) => t || "-",
    },
    {
      title: "PO Amount",
      dataIndex: "poAmount",
      key: "poAmount",
      align: "center",
      render: (amt) => (amt != null ? `₹ ${formatIndianNumber(amt)}` : "-"),
    },
  ];

  const getPoHistoryColumns = (po) => [
    {
      title: "PO Date",
      key: "poDate",
      align: "center",
      render: (_, row) =>
        row?.poDate ? moment(row.poDate).format(DATE_FORMAT) : "-",
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      align: "center",
      render: (t) => t || "-",
    },
    {
      title: "PO Amount",
      dataIndex: "poAmount",
      key: "poAmount",
      align: "center",
      render: (amt) => (amt != null ? `₹ ${formatIndianNumber(amt)}` : "-"),
    },
    {
      title: "Duration",
      key: "duration",
      align: "center",
      render: (_, row) => {
        const start = row?.startDate
          ? moment(row.startDate).format(DATE_FORMAT)
          : "-";
        const end = row?.endDate
          ? moment(row.endDate).format(DATE_FORMAT)
          : "-";
        return start !== "-" || end !== "-" ? `${start} to ${end}` : "-";
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 90,
      render: (_, row, index) => (
        <Button
          type="link"
          size="small"
          disabled={
            savingHistory ||
            (editingHistory &&
              editingHistory.poId === po._id &&
              editingHistory.index === index)
          }
          onClick={() => startEditHistory(po, row, index)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const renderExpandedRow = (record) => {
    const poId = record._id;
    const form = getFormValuesForPo(poId);
    const isSaving = savingPoId === poId;
    const startDayjs = toDayjsOrNull(form.startDate);
    const endDayjs = toDayjsOrNull(form.endDate);
    const rangeValue = startDayjs || endDayjs ? [startDayjs, endDayjs] : null;
    const poHistory = Array.isArray(record?.poHistory) ? record.poHistory : [];

    return (
      <div style={{ padding: "12px 24px", background: "#fafafa" }}>
        {poHistory.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              PO History
            </div>
            <Table
              dataSource={poHistory}
              columns={getPoHistoryColumns(record)}
              rowKey={(_, i) => `${poId}-history-${i}`}
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
              components={{
                header: {
                  cell: PoHistoryHeaderCell,
                },
              }}
            />
          </div>
        )}
        {editingHistory && editingHistory.poId === poId ? (
          <>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
              Edit PO History Entry {editingHistory.index + 1}
            </div>
            <Row gutter={[32, 12]} align="middle" wrap>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>PO Date</span>
                <DatePicker
                  value={historyForm.poDate}
                  onChange={(d) =>
                    setHistoryForm((prev) => ({ ...prev, poDate: d }))
                  }
                  format="DD-MM-YYYY"
                  style={{ width: 140 }}
                  allowClear={false}
                  getPopupContainer={(node) =>
                    node?.parentNode || document.body
                  }
                />
              </Col>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>Invoice No.</span>
                <Input
                  placeholder="Invoice No."
                  value={historyForm.invoiceNo}
                  onChange={(e) =>
                    setHistoryForm((prev) => ({
                      ...prev,
                      invoiceNo: e.target.value,
                    }))
                  }
                  style={{ width: 160 }}
                />
              </Col>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>PO Amount</span>
                <InputNumber
                  placeholder="PO Amount"
                  value={historyForm.poAmount}
                  onChange={(val) =>
                    setHistoryForm((prev) => ({ ...prev, poAmount: val }))
                  }
                  style={{ width: 120 }}
                  min={0}
                />
              </Col>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>Duration</span>
                <DatePicker.RangePicker
                  value={
                    historyForm.startDate && historyForm.endDate
                      ? [historyForm.startDate, historyForm.endDate]
                      : null
                  }
                  onChange={(dates) =>
                    setHistoryForm((prev) => ({
                      ...prev,
                      startDate: dates?.[0] ?? null,
                      endDate: dates?.[1] ?? null,
                    }))
                  }
                  format="DD-MM-YYYY"
                  placeholder={["Start date", "End date"]}
                  style={{ width: 260 }}
                  getPopupContainer={(node) =>
                    node?.parentNode || document.body
                  }
                />
              </Col>
              <Col flex="0 0 auto">
                <Button
                  type="primary"
                  onClick={handleSaveHistory}
                  loading={savingHistory}
                >
                  Save
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={cancelEditHistory}
                  disabled={savingHistory}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </>
        ) : addFormOpenForPoId === poId ? (
          <>
            <div
              style={{
                marginBottom: 8,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              Add PO Amount
            </div>
            <Row gutter={[32, 0]} align="middle" wrap={false}>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>Invoice No.</span>
                <Input
                  placeholder="Invoice No."
                  value={form.invoiceNo}
                  onChange={(e) =>
                    setFormValueForPo(poId, "invoiceNo", e.target.value)
                  }
                  style={{ width: 140 }}
                />
              </Col>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>PO Amount</span>
                <InputNumber
                  placeholder="PO Amount"
                  value={form.poAmount}
                  onChange={(val) => setFormValueForPo(poId, "poAmount", val)}
                  style={{ width: 120 }}
                  min={0}
                />
              </Col>
              <Col flex="0 0 auto" style={{ marginRight: 24 }}>
                <span style={{ marginRight: 10 }}>Duration</span>
                <DatePicker.RangePicker
                  value={rangeValue}
                  onChange={(dates) => setFormDateRangeForPo(poId, dates)}
                  format="DD-MM-YYYY"
                  placeholder={["Start date", "End date"]}
                  style={{ width: 260 }}
                  allowClear
                  getPopupContainer={(node) =>
                    node?.parentNode || document.body
                  }
                />
              </Col>
              <Col flex="0 0 auto">
                <Button
                  type="primary"
                  onClick={() => handleUpdatePO(record)}
                  loading={isSaving}
                >
                  Save
                </Button>
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => setAddFormOpenForPoId(null)}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <Button
            type="link"
            onClick={() => setAddFormOpenForPoId(poId)}
            style={{ paddingLeft: 0, fontWeight: 600, fontSize: 13 }}
          >
            Add PO Amount
          </Button>
        )}
      </div>
    );
  };

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "0px 20px",
      }}
    >
      <div style={{ padding: "20px 0" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
            }}
          >
          <Tabs
            activeKey={activeStatusTab}
            onChange={setActiveStatusTab}
            items={[
              { key: "pending", label: "Pending" },
              { key: "completed", label: "Completed" },
            ]}
            style={{ marginBottom: -16 }}
          />
          <Space
            align="center"
            wrap
            size="middle"
            style={{ marginLeft: "auto" }}
          >
            {/* Filter by Party Name - commented out
            <Select
              placeholder="Filter by Party Name"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedParentCompanyId || undefined}
              onChange={(val) => setSelectedParentCompanyId(val ?? null)}
              style={{ minWidth: 200 }}
              options={parentCompanies.map((p) => ({
                label: p.name,
                value: p._id,
              }))}
            />
            */}

            <Select
              placeholder="Filter by Suffix"
              allowClear
              value={selectedSuffix || undefined}
              onChange={(val) => setSelectedSuffix(val ?? null)}
              style={{ minWidth: 140 }}
              mode="multiple" 
            >
              <Select.Option value="HO">HO</Select.Option>
              <Select.Option value="PRN">PRN</Select.Option>
              <Select.Option value="PRD">PRD</Select.Option>
              <Select.Option value="OTH">OTH</Select.Option>
              <Select.Option value="BR">BR</Select.Option>
            </Select>
            <DatePicker.RangePicker
              value={dateRangeFilter}
              onChange={(dates) => setDateRangeFilter(dates)}
              format="DD-MM-YYYY"
              allowClear
              style={{ width: 220 }}
            />
            <Select
              placeholder="Filter by Brand Name"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedBrandId || undefined}
              onChange={(val) => setSelectedBrandId(val ?? null)}
              style={{ minWidth: 200 }}
              options={brands.map((b) => ({
                label: b.brandName,
                value: b._id,
              }))}
            />
            <Select
              placeholder="Filter by Vendor"
              allowClear
              showSearch
              optionFilterProp="label"
              value={selectedVendorName || undefined}
              onChange={(val) => setSelectedVendorName(val ?? null)}
              style={{ minWidth: 200 }}
              options={vendors.map((v) => ({
                label: v.name,
                value: v.name,
              }))}
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadExcel}
              loading={downloading}
            >
              Download Excel
            </Button>
          </Space>
        </div>
        <Spin spinning={loading}>
          <Table
            dataSource={poList}
            columns={columns}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
            }}
            expandable={{
              expandedRowRender: renderExpandedRow,
              expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
            }}
            components={{
              header: {
                cell: CustomHeaderCell,
              },
            }}
          />
        </Spin>
        <Modal
          title={`Inventories - ${selectedPoNumber}`}
          open={viewModalOpen}
          onCancel={() => setViewModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setViewModalOpen(false)}>
              Close
            </Button>,
          ]}
          width={900}
        >
          <Table
            dataSource={selectedPoInventories}
            columns={inventoryModalColumns}
            rowKey="_id"
            pagination={false}
            size="small"
            components={{
              header: {
                cell: CustomHeaderCell,
              },
            }}
          />
        </Modal>
        <Modal
          title={`PO History - ${selectedPoHistoryPoNumber}`}
          open={poHistoryModalOpen}
          onCancel={() => setPoHistoryModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setPoHistoryModalOpen(false)}>
              Close
            </Button>,
          ]}
          width={520}
        >
          <Table
            dataSource={selectedPoHistory}
            columns={poHistoryViewColumns}
            rowKey={(_, i) => `po-history-view-${i}`}
            pagination={false}
            size="small"
            locale={{ emptyText: "No PO history found" }}
            components={{
              header: {
                cell: PoHistoryHeaderCell,
              },
            }}
          />
        </Modal>
        <Modal
          title={<div style={{ marginBottom: "2rem" }}>Purchase Order Filters</div>}
          open={poPdfModalOpen}
          onCancel={closePoPdfFiltersModal}
          width={600}
          footer={[
            <Button
              key="pdf"
              type="primary"
              onClick={handlePoPdfDownload}
              style={{ marginRight: "8px" }}
            >
              <BsDownload style={{ marginRight: "8px" }} /> PDF
            </Button>,
            <Button key="preview" type="primary" onClick={handlePoPdfPreview}>
              <BsEye style={{ marginRight: "8px" }} /> Preview
            </Button>,
          ]}
        >
          <div>
            <Checkbox
              checked={poPdfFilters.showPeriod}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showPeriod: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show period
            </Checkbox>
          </div>
          <div>
            <Checkbox
              checked={poPdfFilters.showZeroAmountInventories}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showZeroAmountInventories: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show zero amount inventories
            </Checkbox>
          </div>
          <div>
            <Checkbox
              checked={poPdfFilters.showItemAmount}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showItemAmount: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show total billing amount only
            </Checkbox>
          </div>
          <div>
            <Checkbox
              checked={poPdfFilters.showSeparateProductionCharges}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showSeparateProductionCharges: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show printing and mounting separately
            </Checkbox>
          </div>
          <div>
            <Checkbox
              checked={poPdfFilters.showHoardingWisePrintingMounting}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showHoardingWisePrintingMounting: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show hoarding wise printing & mounting
            </Checkbox>
          </div>
          <div>
            <Checkbox
              checked={poPdfFilters.showPrintingMountingOnly}
              onChange={(e) =>
                setPoPdfFilters((prev) => ({
                  ...prev,
                  showPrintingMountingOnly: e.target.checked,
                }))
              }
              style={{ marginBottom: "10px" }}
            >
              Show printing & mounting only
            </Checkbox>
          </div>
        </Modal>
        <Modal
          title="Purchase Order Preview"
          open={poPdfPreviewOpen}
          onCancel={closePoPdfPreviewModal}
          footer={null}
          width="fit-content"
          centered
        >
          {poPdfPreviewOpen && (
            <iframe
              ref={poPdfPreviewRef}
              title="Purchase order PDF preview"
              style={{
                width: "310mm",
                height: "197mm",
                border: "none",
                margin: "0 auto",
                display: "block",
                boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
              }}
            />
          )}
        </Modal>
        <Modal
          title="Update Vendor to"
          open={editVendorModalOpen}
          onCancel={closeEditVendorModal}
          onOk={handleUpdateVendor}
          okText="Update Vendor"
          confirmLoading={updatingVendor}
          okButtonProps={{
            disabled: !editingVendorName || updatingVendor,
          }}
        >
          <Select
            placeholder="Select vendor"
            showSearch
            optionFilterProp="label"
            value={editingVendorName || undefined}
            onChange={setEditingVendorName}
            filterOption={(input, option) =>
              (option?.label || "")
                .toLowerCase()
                .startsWith(input.toLowerCase())
            }
            style={{ width: "100%" }}
            loading={tradingPartiesLoading}
            options={tradingPartyOptions.map((party) => ({
              label: party.name,
              value: party.name,
            }))}
          />
        </Modal>
      </div>
    </div>
  );
};

export default PurchaseOrderContainer;
